import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronDown, ChevronRight } from 'lucide-react';
import ProductGroupCard from '@/components/ProductGroupCard';
import { ProductCardSkeleton } from '@/components/Skeletons';
import { getProductGroupsPaginated } from '@/lib/api';
import { useRegion } from '@/context/RegionContext';
import { useCategories } from '@/hooks/useApiQueries';
import SEO from '@/components/SEO';
import SortDropdown from '@/components/SortDropdown';
import FilterCheckbox from '@/components/common/FilterCheckbox';
import FilterSidebar from '@/components/common/FilterSidebar';
import ScrollToTopButton from '@/components/ScrollToTopButton';
import AdPlaceholder from '@/components/AdPlaceholder';

const ITEMS_PER_PAGE = 12;

// Hook to check if screen is md or larger (protected from browser extension CSS conflicts)
const useIsMdScreen = () => {
  const [isMd, setIsMd] = useState(window.innerWidth >= 768);
  useEffect(() => {
    const handleResize = () => setIsMd(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMd;
};

// Helper to build search params for API call
const buildSearchParams = ({ query, productTags, groupTags, selectedTag, tagType, selectedCategory, sortBy, region, limit, offset }) => {
  const params = { limit, offset, region };
  if (query) params.search = query;
  
  // Handle multiple product_tags (OR filter)
  if (productTags) {
    params.product_tags = productTags;
  }
  if (groupTags) {
    params.group_tags = groupTags;
  }
  // Single tag selection (legacy/sidebar) - only if no multi-tags
  if (!productTags && !groupTags && selectedTag) {
    if (tagType === 'group') {
      params.group_tag = selectedTag;
    } else {
      params.product_tag = selectedTag;
    }
  }
  
  if (selectedCategory) params.category = selectedCategory;
  if (sortBy) params.sort_by = sortBy;
  
  return params;
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const selectedTag = searchParams.get('tag') || '';
  // Support multiple tags (comma-separated) for OR filtering from Related Products
  const productTags = searchParams.get('product_tags') || '';
  const groupTags = searchParams.get('group_tags') || '';
  const selectedCategory = searchParams.get('category') || '';
  const tagType = searchParams.get('tagType') || 'product'; // 'product' or 'group'
  const sortBy = searchParams.get('sort') || 'newest';
  const { region } = useRegion();
  const isMdScreen = useIsMdScreen();
  
  const [productGroups, setProductGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState(query);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  
  // Track the active category for checkbox selection (only one category can have selections at a time)
  const [activeFilterCategory, setActiveFilterCategory] = useState(null);
  
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  // Use React Query for cached categories
  const { data: allCategories = [] } = useCategories();
  
  // State for available product tags per category/subcategory (fetched from API)
  const [availableTagsByCategory, setAvailableTagsByCategory] = useState({});
  const [availableTagsBySubcategory, setAvailableTagsBySubcategory] = useState({});
  const [loadingTags, setLoadingTags] = useState(false);
  
  // Filter to only categories with show_on_search enabled
  const categories = useMemo(() => {
    const searchCategories = allCategories.filter(cat => cat.show_on_search === true);
    // Auto-expand all categories by default
    if (searchCategories.length > 0 && Object.keys(expandedCategories).length === 0) {
      const expanded = {};
      searchCategories.forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedCategories(expanded);
    }
    return searchCategories;
  }, [allCategories]);

  // Fetch available product tags for each category (tags actually used by products)
  useEffect(() => {
    const fetchAvailableTags = async () => {
      if (categories.length === 0) return;
      
      setLoadingTags(true);
      const catTags = {};
      const subTags = {};
      
      try {
        await Promise.all(categories.map(async (cat) => {
          // Fetch tags for the category
          try {
            const catRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/product-groups/available-tags?category=${cat.id}&region=${region || ''}`);
            if (catRes.ok) {
              catTags[cat.id] = await catRes.json();
            }
          } catch (e) {
            catTags[cat.id] = [];
          }
          
          // If category uses subcategories mode, fetch tags per subcategory
          if (cat.search_menu_type === 'subcategories' && cat.subcategories) {
            await Promise.all(cat.subcategories.map(async (sub) => {
              try {
                const subRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/product-groups/available-tags?category=${cat.id}&subcategory=${sub.id}&region=${region || ''}`);
                if (subRes.ok) {
                  subTags[`${cat.id}:${sub.id}`] = await subRes.json();
                }
              } catch (e) {
                subTags[`${cat.id}:${sub.id}`] = [];
              }
            }));
          }
        }));
        
        setAvailableTagsByCategory(catTags);
        setAvailableTagsBySubcategory(subTags);
      } catch (error) {
        console.error('Failed to fetch available tags:', error);
      } finally {
        setLoadingTags(false);
      }
    };
    
    fetchAvailableTags();
  }, [categories, region]);

  // Parse current selected tags from URL into arrays for checkbox state
  const selectedProductTagsArray = useMemo(() => 
    productTags ? productTags.split(',').map(t => t.trim().toLowerCase()) : [],
    [productTags]
  );
  const selectedGroupTagsArray = useMemo(() => 
    groupTags ? groupTags.split(',').map(t => t.trim().toLowerCase()) : [],
    [groupTags]
  );

  useEffect(() => {
    // Always load products - even without filters (shows all products)
    searchProductGroups();
  }, [query, selectedTag, productTags, groupTags, selectedCategory, tagType, sortBy, region]);

  // Sync activeFilterCategory from URL on initial load
  useEffect(() => {
    if (productTags || groupTags) {
      // Find which category these tags belong to based on available tags
      const allTags = [...selectedProductTagsArray, ...selectedGroupTagsArray];
      if (allTags.length > 0) {
        // Try to find the category that contains these tags
        for (const cat of categories) {
          const catTags = (availableTagsByCategory[cat.id] || []).map(t => t.toLowerCase());
          
          // Also check subcategory tags
          let subcatTags = [];
          if (cat.subcategories) {
            cat.subcategories.forEach(sub => {
              const subTagKey = `${cat.id}:${sub.id}`;
              const subTags = (availableTagsBySubcategory[subTagKey] || []).map(t => t.toLowerCase());
              subcatTags = [...subcatTags, ...subTags];
            });
          }
          
          const allCatTags = [...catTags, ...subcatTags];
          
          if (allTags.some(t => allCatTags.includes(t))) {
            setActiveFilterCategory(cat.id);
            break;
          }
        }
      }
    }
  }, [categories, productTags, groupTags, availableTagsByCategory, availableTagsBySubcategory]);

  // Reset and load when search params change
  const searchProductGroups = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setProductGroups([]);
    setHasMore(true);
    offsetRef.current = 0;
    
    try {
      const params = buildSearchParams({
        query, productTags, groupTags, selectedTag, tagType, selectedCategory, sortBy, region,
        limit: ITEMS_PER_PAGE, offset: 0
      });
      
      const response = await getProductGroupsPaginated(params);
      setProductGroups(response.items || []);
      setTotal(response.total || 0);
      setHasMore(response.has_more || false);
      offsetRef.current = response.items?.length || 0;
    } catch (error) {
      console.error('Error searching product groups:', error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // Load more items
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoadingMore(true);

    try {
      const params = buildSearchParams({
        query, productTags, groupTags, selectedTag, tagType, selectedCategory, sortBy, region,
        limit: ITEMS_PER_PAGE, offset: offsetRef.current
      });
      
      const response = await getProductGroupsPaginated(params);
      const newItems = response.items || [];
      setProductGroups(prev => [...prev, ...newItems]);
      setHasMore(response.has_more || false);
      offsetRef.current += newItems.length;
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [query, selectedTag, productTags, groupTags, selectedCategory, tagType, sortBy, region, hasMore]);

  // Setup Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loadingRef.current) {
          loadMore();
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    );

    observerRef.current = observer;

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, hasMore]);

  // Update sentinel ref
  useEffect(() => {
    if (sentinelRef.current && observerRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }
  }, [productGroups]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      // Preserve existing tag filters when searching
      const newParams = { q: searchInput.trim() };
      if (productTags) newParams.product_tags = productTags;
      if (groupTags) newParams.group_tags = groupTags;
      if (selectedTag) newParams.tag = selectedTag;
      if (selectedCategory) newParams.category = selectedCategory;
      if (tagType && tagType !== 'product') newParams.tagType = tagType;
      setSearchParams(newParams);
    }
  };

  // Handle checkbox tag selection - multi-select within same category, clear when switching categories
  const handleTagCheckbox = (tag, categoryId, isSubcategoryTag = false) => {
    const tagLower = tag.toLowerCase();
    const currentTags = isSubcategoryTag ? selectedGroupTagsArray : selectedProductTagsArray;
    const tagParamKey = isSubcategoryTag ? 'group_tags' : 'product_tags';
    
    // Check if we're switching categories
    const isSwitchingCategory = activeFilterCategory && activeFilterCategory !== categoryId;
    
    let newTags;
    if (isSwitchingCategory) {
      // Switching categories - start fresh with just this tag, clear ALL previous tags
      newTags = [tagLower];
      setActiveFilterCategory(categoryId);
    } else {
      // Same category - toggle the tag
      if (currentTags.includes(tagLower)) {
        // Remove tag
        newTags = currentTags.filter(t => t !== tagLower);
      } else {
        // Add tag
        newTags = [...currentTags, tagLower];
      }
      
      if (newTags.length > 0) {
        setActiveFilterCategory(categoryId);
      } else {
        setActiveFilterCategory(null);
      }
    }
    
    // Build new URL params - when switching categories, clear all tag params
    const newParams = {};
    if (query) newParams.q = query;
    if (sortBy) newParams.sort = sortBy;
    
    if (newTags.length > 0) {
      newParams[tagParamKey] = newTags.join(',');
      // When switching categories, don't keep any old tag params
      // When staying in same category, also don't keep the other tag type 
      // (since we only allow selections within one category at a time)
    }
    
    setSearchParams(newParams);
  };

  // Check if a tag is selected
  const isTagSelected = (tag, isSubcategoryTag = false) => {
    const tagLower = tag.toLowerCase();
    return isSubcategoryTag 
      ? selectedGroupTagsArray.includes(tagLower)
      : selectedProductTagsArray.includes(tagLower);
  };

  const handleTagClick = (tag, categorySlug, isSubcategoryTag = false) => {
    const params = { tag, category: categorySlug };
    if (isSubcategoryTag) {
      params.tagType = 'group';
    }
    setSearchParams(params);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchParams({});
    setProductGroups([]);
    setTotal(0);
    setHasMore(false);
  };

  const handleSortChange = (newSort) => {
    const newParams = {};
    if (query) newParams.q = query;
    if (productTags) newParams.product_tags = productTags;
    if (groupTags) newParams.group_tags = groupTags;
    if (selectedTag) newParams.tag = selectedTag;
    if (selectedCategory) newParams.category = selectedCategory;
    if (tagType && tagType !== 'product') newParams.tagType = tagType;
    if (newSort) newParams.sort = newSort;
    setSearchParams(newParams);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleSubcategory = (subcategoryId) => {
    setExpandedSubcategories(prev => ({
      ...prev,
      [subcategoryId]: !prev[subcategoryId]
    }));
  };

  return (
    <div className="min-h-screen" data-testid="search-page">
      <SEO 
        title={query ? `Search: ${query}` : selectedTag ? `Tagged: ${selectedTag}` : 'Shop All'}
        description="Search and discover curated Amazon finds for the cool girls. Filter by category, tags, and style."
        url="/search"
        noIndex={true}
      />
      
      {/* Search Header */}
      <div className="px-10 md:px-20 lg:px-28 xl:px-36 py-8 md:py-12 border-b border-site-border-strong bg-site-bg-alt">
        <h1 className="font-heading text-3xl md:text-4xl italic mb-6">
          Search
        </h1>
        
        {/* Search Form */}
        <form onSubmit={handleSearch} className="relative max-w-2xl">
          <div className="flex border-2 border-site-border-strong bg-white">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search for looks, styles, or tags..."
              className="flex-1 px-4 py-3 font-body text-base outline-none bg-transparent"
              data-testid="search-input"
            />
            {searchInput && (
              <button
                type="button"
                onClick={clearSearch}
                className="px-3 flex items-center hover:bg-site-bg-alt transition-colors"
                data-testid="clear-search"
              >
                <X className="w-5 h-5" />
              </button>
            )}
            <button
              type="submit"
              className="px-6 flex items-center bg-site-bg-dark text-site-text-inverse hover:bg-site-accent-blue transition-colors"
              data-testid="search-submit"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>
        </form>

        {/* Filtering by: tags (only show when tags exist) */}
        {(selectedTag || productTags || groupTags) && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="active-tag-filters">
            <span className="text-sm text-site-text-muted">Filtering by:</span>
            
            {/* Single tag filter (from sidebar) */}
            {selectedTag && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-site-bg-dark text-site-text-inverse text-xs font-mono uppercase">
                {selectedTag}
                <button 
                  onClick={() => {
                    const newParams = {};
                    if (query) newParams.q = query;
                    setSearchParams(newParams);
                  }}
                  className="ml-1 hover:text-red-300"
                  data-testid="clear-tag-filter"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            
            {/* Multiple product tags (from Related Products "View All") */}
            {productTags && productTags.split(',').map((tag, idx) => (
              <span 
                key={`product-tag-${idx}`}
                className="inline-flex items-center gap-1 px-3 py-1 bg-site-bg-dark text-site-text-inverse text-xs font-mono uppercase"
              >
                {tag.trim()}
                <button 
                  onClick={() => {
                    const remainingTags = productTags.split(',').filter((_, i) => i !== idx).join(',');
                    const newParams = {};
                    if (query) newParams.q = query;
                    if (groupTags) newParams.group_tags = groupTags;
                    if (remainingTags) newParams.product_tags = remainingTags;
                    setSearchParams(newParams);
                  }}
                  className="ml-1 hover:text-red-300"
                  data-testid={`clear-product-tag-${idx}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            
            {/* Multiple group/subcategory tags (from Related Products "View All") */}
            {groupTags && groupTags.split(',').map((tag, idx) => (
              <span 
                key={`group-tag-${idx}`}
                className="inline-flex items-center gap-1 px-3 py-1 bg-site-bg-dark text-site-text-inverse text-xs font-mono uppercase"
              >
                {tag.trim()}
                <button 
                  onClick={() => {
                    const remainingTags = groupTags.split(',').filter((_, i) => i !== idx).join(',');
                    const newParams = {};
                    if (query) newParams.q = query;
                    if (productTags) newParams.product_tags = productTags;
                    if (remainingTags) newParams.group_tags = remainingTags;
                    setSearchParams(newParams);
                  }}
                  className="ml-1 hover:text-red-300"
                  data-testid={`clear-group-tag-${idx}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            
            {/* Clear all tags button when multiple tags exist */}
            {((productTags && productTags.includes(',')) || (groupTags && groupTags.includes(',')) || (productTags && groupTags)) && (
              <button
                onClick={() => {
                  const newParams = {};
                  if (query) newParams.q = query;
                  setSearchParams(newParams);
                }}
                className="text-xs text-site-text-muted hover:text-site-text underline ml-2"
                data-testid="clear-all-tag-filters"
              >
                Clear all
              </button>
            )}
          </div>
        )}

        {/* Searched by: query (only show when search query exists) */}
        {query && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="active-search-query">
            <span className="text-sm text-site-text-muted">Searched by:</span>
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-site-bg-dark text-site-text-inverse text-xs font-mono">
              "{query}"
              <button 
                onClick={() => {
                  setSearchInput('');
                  const newParams = {};
                  if (productTags) newParams.product_tags = productTags;
                  if (groupTags) newParams.group_tags = groupTags;
                  if (selectedTag) newParams.tag = selectedTag;
                  if (selectedCategory) newParams.category = selectedCategory;
                  if (Object.keys(newParams).length > 0) {
                    setSearchParams(newParams);
                  } else {
                    setSearchParams({});
                    setProductGroups([]);
                    setTotal(0);
                    setHasMore(false);
                  }
                }}
                className="ml-1 hover:text-red-300"
                data-testid="clear-search-query"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          </div>
        )}
      </div>

      <div className="flex">
        {/* Category Menus Sidebar - protected from browser extension CSS conflicts */}
        {categories.length > 0 && isMdScreen && (
          <FilterSidebar minHeight="min-h-[calc(100vh-200px)]" style={{ display: 'block' }}>
            <h2 className="font-mono text-xs uppercase tracking-widest text-site-text-muted mb-4">
              Browse by Category
            </h2>
            
            {categories.map((category) => (
              <div key={category.id} className="mb-4">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full flex items-center justify-between py-2 font-medium text-sm hover:text-site-accent-blue transition-colors"
                >
                  <span>{category.title}</span>
                  {expandedCategories[category.id] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {/* Category Content */}
                {expandedCategories[category.id] && (
                  <div className="ml-2 mt-1 border-l border-site-border pl-3">
                    {category.search_menu_type === 'subcategories' ? (
                      // Show tags grouped by subcategory
                      (category.subcategories || []).map((sub) => {
                        const subTagKey = `${category.id}:${sub.id}`;
                        const subTags = availableTagsBySubcategory[subTagKey] || [];
                        
                        return (
                          <div key={sub.id} className="mb-3">
                            <button
                              onClick={() => toggleSubcategory(sub.id)}
                              className="w-full flex items-start justify-between py-1 text-xs font-mono uppercase tracking-wide text-site-text-muted hover:text-site-text transition-colors text-left"
                            >
                              <span className="text-left flex-1 pr-2">{sub.name}</span>
                              <ChevronRight className={`w-3 h-3 flex-shrink-0 mt-0.5 transition-transform ${expandedSubcategories[sub.id] ? 'rotate-90' : ''}`} />
                            </button>
                            {expandedSubcategories[sub.id] && (
                              <div className="mt-1 space-y-0.5">
                                {subTags.length > 0 ? (
                                  [...subTags].sort((a, b) => a.localeCompare(b)).map((tag) => (
                                    <FilterCheckbox
                                      key={tag}
                                      label={tag}
                                      checked={isTagSelected(tag, false)}
                                      onChange={() => handleTagCheckbox(tag, category.id, false)}
                                    />
                                  ))
                                ) : (
                                  <span className="text-xs text-site-text-muted italic">No products yet</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      // Show product tags directly - sorted alphabetically
                      <div className="space-y-0.5">
                        {(availableTagsByCategory[category.id] || []).length > 0 ? (
                          [...(availableTagsByCategory[category.id] || [])].sort((a, b) => a.localeCompare(b)).map((tag) => (
                            <FilterCheckbox
                              key={tag}
                              label={tag}
                              checked={isTagSelected(tag, false)}
                              onChange={() => handleTagCheckbox(tag, category.id, false)}
                            />
                          ))
                        ) : (
                          <span className="text-xs text-site-text-muted italic">No products yet</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </FilterSidebar>
        )}

        {/* Main Content */}
        <main className="flex-1">
          {/* Results Section - Always show products */}
          <div className="px-4 md:px-8 pt-6 pb-8">
            <div className="mb-6 flex items-center justify-between">
              <p className="font-mono text-xs text-site-text-muted uppercase tracking-widest">
                {loading ? 'Loading...' : `${productGroups.length}${hasMore ? '+' : ''} of ${total} ${total === 1 ? 'Item' : 'Items'}`}
              </p>
              
              {/* Sort Dropdown - using shared component */}
              <SortDropdown value={sortBy} onChange={handleSortChange} />
            </div>

            {/* Product Groups Grid */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
              ) : productGroups.length === 0 ? (
                <div className="text-center py-24">
                  <p className="font-heading text-2xl italic mb-4">No items found</p>
                  <p className="font-body text-site-text-muted">
                    Try adjusting your search or browse the categories
                  </p>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {productGroups.map((group) => (
                      <ProductGroupCard key={group.id} group={group} />
                    ))}
                    
                    {/* Loading more skeletons */}
                    {loadingMore && (
                      <>
                        {Array.from({ length: 4 }).map((_, i) => (
                          <ProductCardSkeleton key={`loading-${i}`} />
                        ))}
                      </>
                    )}
                  </div>
                  
                  {/* Sentinel element for infinite scroll */}
                  {hasMore && !loadingMore && (
                    <div 
                      ref={sentinelRef} 
                      className="h-20 flex items-center justify-center"
                      data-testid="search-infinite-scroll-sentinel"
                    >
                      <span className="text-site-text-muted text-sm font-mono uppercase tracking-widest">
                        Loading more...
                      </span>
                    </div>
                  )}
                  
                  {/* End of results */}
                  {!hasMore && productGroups.length > 0 && (
                    <div className="py-8 text-center">
                      <p className="text-site-text-muted text-sm font-mono uppercase tracking-widest">
                        Showing all {total} items
                      </p>
                    </div>
                  )}
                  
                  {/* Ad at bottom of search results */}
                  {productGroups.length > 0 && (
                    <div className="-mt-2">
                      <AdPlaceholder slot="in-feed" />
                    </div>
                  )}
                </div>
              )}
            </div>
        </main>
      </div>
      <ScrollToTopButton />
    </div>
  );
};

export default SearchPage;
